import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { missionService } from '../services/missionService';
import { MissionCommandCenter } from '../types/mission';

const confidenceLabel: Record<string, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Media',
  HIGH: 'Alta',
};

export function MissionCommandCenterPage() {
  const { id } = useParams<{ id: string }>();
  const [payload, setPayload] = useState<MissionCommandCenter | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadCommandCenter = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await missionService.getMissionCommandCenter(id);
      setPayload(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar centro de comando';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCommandCenter();
  }, [id]);

  const handleApprove = async (approvalId: string) => {
    if (!id) return;
    try {
      setActionLoading(approvalId);
      await missionService.approveMission(id, approvalId);
      toast.success('Aprovacao concluida');
      await loadCommandCenter();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao aprovar');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (approvalId: string) => {
    if (!id) return;
    try {
      setActionLoading(approvalId);
      await missionService.rejectMission(id, approvalId, 'Rejeitado manualmente pelo usuario');
      toast.success('Aprovacao rejeitada');
      await loadCommandCenter();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao rejeitar');
    } finally {
      setActionLoading(null);
    }
  };

  const mission = payload?.mission;
  const latestDiagnosis = payload?.latestDiagnosis;
  const activePlan = payload?.activePlan;
  const pendingApprovals = payload?.pendingApprovals ?? [];
  const recentEvents = payload?.recentEvents ?? [];
  const outcome = payload?.outcome;

  const headline = useMemo(() => mission?.title || 'Centro de comando da missao', [mission]);

  return (
    <>
      <Header
        title={headline}
        subtitle="Supervisione progresso, risco, aprovacoes e o que mudou sem precisar navegar pela operacao."
        actions={(
          <div className="flex gap-3">
            <Link to="/missions" className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
              Voltar para missoes
            </Link>
            <Link to={`/crm?missionId=${mission?.id || ''}`} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800">
              Abrir operacao
            </Link>
          </div>
        )}
      />

      <div className="p-6 space-y-6">
        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500 shadow-sm">
            Carregando centro de comando...
          </div>
        ) : error || !payload || !mission ? (
          <div className="rounded-3xl border border-red-200 bg-red-50 p-8 shadow-sm">
            <p className="text-sm text-red-700">{error || 'Missao nao encontrada'}</p>
            <button onClick={loadCommandCenter} className="mt-4 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white">
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <section className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Missao atual</p>
                <h2 className="mt-3 text-2xl font-semibold text-slate-950">{mission.title}</h2>
                <p className="mt-3 text-sm leading-6 text-slate-600">{mission.goal}</p>

                <div className="mt-5 flex flex-wrap gap-4 text-sm text-slate-500">
                  <span>
                    Prazo:{' '}
                    <strong className="text-slate-800">
                      {mission.deadline ? new Date(mission.deadline).toLocaleDateString('pt-BR') : 'Nao informado'}
                    </strong>
                  </span>
                  <span>
                    Prioridade: <strong className="text-slate-800">{mission.priority}</strong>
                  </span>
                  <span>
                    Status: <strong className="text-slate-800">{mission.status}</strong>
                  </span>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <p className="text-sm text-slate-500">Chance de sucesso</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-950">{payload.metrics.successProbability ?? 0}%</p>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                  <p className="text-sm text-slate-500">Confianca da leitura</p>
                  <p className="mt-2 text-3xl font-semibold text-slate-950">
                    {confidenceLabel[payload.metrics.confidenceLevel || 'LOW'] || payload.metrics.confidenceLevel}
                  </p>
                </div>
              </div>
            </section>

            <section className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Como estamos</p>
                <p className="mt-3 text-sm text-slate-600">
                  {latestDiagnosis?.summary || 'Ainda nao existe diagnostico registrado para esta missao.'}
                </p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Gargalo principal</p>
                <p className="mt-3 text-sm text-slate-600">
                  {latestDiagnosis?.primaryBottleneck || 'Nao identificado ainda'}
                </p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">O que o LeadNexia esta fazendo</p>
                <p className="mt-3 text-sm text-slate-600">
                  {activePlan?.summary || 'Nenhum plano ativo ainda.'}
                </p>
              </div>
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Profundidade operacional</p>
                <p className="mt-3 text-sm text-slate-600">
                  {payload.metrics.campaignCount} campanha(s) vinculada(s) a esta missao.
                </p>
              </div>
            </section>

            <section className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-slate-950">O que precisa de voce</h3>
                  <p className="mt-1 text-sm text-slate-500">Aprovacoes e decisoes humanas pendentes.</p>
                </div>

                {pendingApprovals.length === 0 ? (
                  <div className="px-6 py-8 text-sm text-slate-500">Nenhuma aprovacao pendente no momento.</div>
                ) : (
                  <div className="divide-y divide-slate-200">
                    {pendingApprovals.map((approval) => (
                      <div key={approval.id} className="px-6 py-5">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-slate-950">{approval.summary}</p>
                            <p className="mt-2 text-sm text-slate-600">{approval.reason || 'Sem motivo adicional informado.'}</p>
                            <p className="mt-3 text-xs text-slate-500">
                              Solicitado em {new Date(approval.requestedAt).toLocaleString('pt-BR')}
                            </p>
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={() => handleReject(approval.id)}
                              disabled={actionLoading === approval.id}
                              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                            >
                              Rejeitar
                            </button>
                            <button
                              onClick={() => handleApprove(approval.id)}
                              disabled={actionLoading === approval.id}
                              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-60"
                            >
                              Aprovar
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-slate-950">O que mudou desde ontem</h3>
                  <p className="mt-1 text-sm text-slate-500">Eventos mais recentes da missao.</p>
                </div>

                <div className="divide-y divide-slate-200">
                  {recentEvents.length === 0 ? (
                    <div className="px-6 py-8 text-sm text-slate-500">Ainda nao existem eventos registrados.</div>
                  ) : (
                    recentEvents.slice(0, 6).map((event) => (
                      <div key={event.id} className="px-6 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{event.summary}</p>
                            <p className="mt-1 text-sm text-slate-600">{event.reason || 'Sem detalhe adicional.'}</p>
                          </div>
                          <span className="shrink-0 text-xs text-slate-400">
                            {new Date(event.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            <section className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-slate-950">Plano e estrategia atual</h3>
                  <p className="mt-1 text-sm text-slate-500">O que esta sendo executado e qual hipotese sustenta a missao.</p>
                </div>
                <div className="space-y-5 px-6 py-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Hipotese principal</p>
                    <p className="mt-2 text-sm text-slate-600">{activePlan?.primaryHypothesis || 'Ainda nao registrada.'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Criterio de sucesso</p>
                    <p className="mt-2 text-sm text-slate-600">{activePlan?.successCriteria || 'Nao definido.'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Criterio de parada ou ajuste</p>
                    <p className="mt-2 text-sm text-slate-600">{activePlan?.stopCriteria || 'Nao definido.'}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 px-6 py-4">
                  <h3 className="text-lg font-semibold text-slate-950">Resultado e retorno</h3>
                  <p className="mt-1 text-sm text-slate-500">Quando existir resultado consolidado, ele aparece aqui.</p>
                </div>
                <div className="space-y-5 px-6 py-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Resultado atual</p>
                    <p className="mt-2 text-sm text-slate-600">{outcome?.resultSummary || 'Missao ainda sem outcome consolidado.'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <p className="text-xs text-slate-500">Custo estimado</p>
                      <p className="mt-2 text-lg font-semibold text-slate-950">
                        {outcome?.estimatedCost != null ? `R$ ${outcome.estimatedCost.toLocaleString('pt-BR')}` : '--'}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <p className="text-xs text-slate-500">ROI estimado</p>
                      <p className="mt-2 text-lg font-semibold text-slate-950">
                        {outcome?.estimatedRoi != null ? `${outcome.estimatedRoi}%` : '--'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </>
  );
}
